// components/calculator/CompanyInfoSection.js
import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Grid, 
  TextField, 
  Autocomplete, 
  Box,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Business, AccessTime, AttachMoney, Search } from '@mui/icons-material';
import { axiosInstance } from '../../utils/axios';

const CompanyInfoSection = ({ formData, setFormData }) => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientError, setClientError] = useState('');

  // Cargar clientes al montar el componente
  useEffect(() => {
    fetchClients();
  }, []);

  // Función para obtener la lista de clientes
  const fetchClients = async () => {
    setLoadingClients(true);
    setClientError('');
    try {
      const response = await axiosInstance.get('/clientes');
      setClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClientError('Error al cargar clientes');
      
      // Datos de ejemplo en caso de error
      setClients([
        {
          id: 1,
          razon_social: 'Empresa Demo S.A.',
          horas: 160,
          monto_base: 150000
        },
        {
          id: 2,
          razon_social: 'Tecnología ABC',
          horas: 80,
          monto_base: 85000
        },
        {
          id: 3,
          razon_social: 'Servicios XYZ Ltda.',
          horas: 120,
          monto_base: 95000
        }
      ]);
    } finally {
      setLoadingClients(false);
    }
  };

  // Función para manejar la selección de cliente
  const handleClientSelect = (event, newValue) => {
    setSelectedClient(newValue);
    
    if (newValue) {
      // Cargar datos del cliente seleccionado
      setFormData(prev => ({
        ...prev,
        nombreEmpresa: newValue.razon_social,
        horasServicio: newValue.horas.toString(),
        montoBase: newValue.monto_base.toString(),
        clienteId: newValue.id // Guardar ID del cliente para referencia
      }));
    } else {
      // Limpiar datos si no hay cliente seleccionado
      setFormData(prev => ({
        ...prev,
        nombreEmpresa: '',
        horasServicio: '',
        montoBase: '',
        clienteId: null
      }));
    }
  };

  // Función para manejar cambios manuales en los campos
  const handleManualChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    
    // Si el usuario edita manualmente, desseleccionar el cliente
    if (selectedClient) {
      setSelectedClient(null);
      setFormData(prev => ({ ...prev, clienteId: null }));
    }
  };

  // Función para formatear la opción en el autocomplete
  const formatClientOption = (option) => {
    if (!option) return '';
    return `${option.razon_social} - ${option.horas}hs - ${option.monto_base?.toLocaleString('es-AR')}`;
  };

  return (
    <>
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        📋 Información de la Empresa
      </Typography>

      {/* Selector de Cliente */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Autocomplete
            value={selectedClient}
            onChange={handleClientSelect}
            options={clients}
            getOptionLabel={(option) => option.razon_social || ''}
            loading={loadingClients}
            loadingText="Cargando clientes..."
            noOptionsText="No se encontraron clientes"
            renderInput={(params) => (
              <TextField
                {...params}
                label="🔍 Seleccionar Cliente Existente"
                placeholder="Buscar cliente por razón social..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    <Business sx={{ mr: 1, fontSize: 16, verticalAlign: 'middle' }} />
                    {option.razon_social}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      size="small" 
                      label={`${option.horas} hs`} 
                      color="info" 
                      variant="outlined" 
                    />
                    <Chip 
                      size="small" 
                      label={`${option.monto_base?.toLocaleString('es-AR')}`} 
                      color="success" 
                      variant="outlined" 
                    />
                  </Box>
                </Box>
              </Box>
            )}
          />
          
          {clientError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {clientError}. Mostrando datos de ejemplo.
            </Alert>
          )}
          
          {selectedClient && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Cliente seleccionado: <strong>{selectedClient.razon_social}</strong>
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Campos de información (editables) */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Nombre de la Empresa"
            value={formData.nombreEmpresa}
            onChange={handleManualChange('nombreEmpresa')}
            InputProps={{
              startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />
            }}
            placeholder="Ej: Empresa ABC S.A."
            helperText={selectedClient ? "Editando datos del cliente seleccionado" : "Ingrese manualmente o seleccione un cliente"}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Horas de Servicio Mensual"
            value={formData.horasServicio}
            onChange={handleManualChange('horasServicio')}
            InputProps={{
              startAdornment: <AccessTime sx={{ mr: 1, color: 'action.active' }} />
            }}
            placeholder="Ej: 160"
            inputProps={{ min: 0, step: 0.5 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Monto Mensual Actual (Sin IVA)"
            value={formData.montoBase}
            onChange={handleManualChange('montoBase')}
            InputProps={{
              startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.active' }} />
            }}
            placeholder="Ej: 100000"
            required
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
      </Grid>

      {/* Información adicional */}
      {selectedClient && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            💡 <strong>Tip:</strong> Los datos se han cargado automáticamente desde el cliente seleccionado. 
            Puedes modificarlos si necesitas hacer ajustes para este cálculo específico.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default CompanyInfoSection;